// zod mirrors of the Puzzle Studio admin API (contracts/studio.md §3 and its "Implementation notes";
// backend/api/src/schemas/studio.ts). Every response is parsed before use, like the rest of the panel.

import { z } from "zod";
import {
  AnswerSchema,
  Category,
  Difficulty,
  isoDate,
  PuzzleRowMode,
  PuzzleSource,
  PuzzleSpecSchema,
  PuzzleStatus,
  ReviewState,
  VerificationResult,
  VerificationSummary,
} from "./schemas";

const count = z.number().int().nonnegative();

// ---------------------------------------------------------------- types (engine catalogue)

export const StudioType = z.object({
  type: z.string(),
  category: z.string(),
  components: z.array(z.string()),
  /** Solver-verified; otherwise every puzzle needs a person to approve it. */
  hasSolver: z.boolean(),
  /** Has a generator: procedural jobs possible. */
  procedural: z.boolean(),
  /** Claude drafts possible (a dedicated prompt, or the generic one for types with a solver). */
  aiSupported: z.boolean(),
  aiMode: z.enum(["dedicated", "generic"]).nullable().optional(),
  promptVersion: z.string().nullable(),
  howTo: z.string().optional(),
});
export type StudioType = z.infer<typeof StudioType>;

export const StudioTypes = z.object({
  types: z.array(StudioType),
  ai: z.object({
    /** studio.aiEnabled (the kill switch). */
    enabled: z.boolean(),
    /** The engine has ANTHROPIC_API_KEY. */
    keyConfigured: z.boolean(),
    /** The engine's STUDIO_DEFAULT_MODEL (jobs use studio.defaultModel). */
    defaultModel: z.string().nullable(),
  }),
});
export type StudioTypes = z.infer<typeof StudioTypes>;

// ---------------------------------------------------------------- jobs

export const JOB_KINDS = ["procedural", "ai", "import", "levelpack"] as const;
export const JobKind = z.enum(JOB_KINDS);
export type JobKind = z.infer<typeof JobKind>;
export const JOB_STATUSES = ["queued", "running", "done", "failed", "cancelled"] as const;
export const JobStatus = z.enum(JOB_STATUSES);
export type JobStatus = z.infer<typeof JobStatus>;
export const ITEM_STATUSES = ["created", "rejected_by_verifier", "needs_review"] as const;
export const ItemStatus = z.enum(ITEM_STATUSES);
export type ItemStatus = z.infer<typeof ItemStatus>;

/** Difficulty score range, 0–1 (easy < 0.4 ≤ medium < 0.65 ≤ hard). */
export const DifficultyRange = z.object({ min: z.number().min(0).max(1), max: z.number().min(0).max(1) });
export type DifficultyRange = z.infer<typeof DifficultyRange>;

export const StudioJob = z.object({
  id: z.string(),
  kind: JobKind,
  type: z.string(),
  status: JobStatus,
  params: z.object({
    count,
    difficulty: DifficultyRange.nullable(),
    model: z.string().nullable(),
    theme: z.string().nullable(),
    /** levelpack jobs (contracts/studio.md §5): the track, the custom mix / curve / seed, and the packs built so far. */
    track: z.string().optional(),
    typeMix: z.record(z.string(), z.number()).nullable().optional(),
    curve: z.object({ start: z.number(), end: z.number() }).nullable().optional(),
    seed: z.string().nullable().optional(),
    packs: z.array(count).optional(),
  }),
  /** Admin email; null for jobs from the CLI (curated import). */
  requestedBy: z.string().nullable(),
  counts: z.object({ requested: count, created: count, failedVerification: count, needsReview: count }),
  /** Claude tokens (input includes cache reads and writes). */
  tokens: z.object({ input: count, output: count }),
  costUsd: z.number().min(0),
  model: z.string().nullable(),
  promptVersion: z.string().nullable(),
  /** Starts with a code: `budget_exceeded: …`, `ai_disabled: …`, `ai_unavailable: …`, `engine_unavailable: …`. */
  error: z.string().nullable(),
  /** A running job stops after its current batch. */
  cancelRequested: z.boolean(),
  createdAt: z.string(),
  startedAt: z.string().nullable(),
  finishedAt: z.string().nullable(),
});
export type StudioJob = z.infer<typeof StudioJob>;

export const StudioJobResult = z.object({ job: StudioJob });
export const StudioJobs = z.object({ items: z.array(StudioJob), nextCursor: z.string().nullable() });
export type StudioJobs = z.infer<typeof StudioJobs>;

/** The bank puzzle an item created, with what the review needs (spec, canonical answer, explanation). */
export const JobItemPuzzle = z.object({
  id: z.string(),
  status: PuzzleStatus,
  mode: PuzzleRowMode,
  date: isoDate.nullable(),
  reviewState: ReviewState,
  spec: PuzzleSpecSchema,
  answer: AnswerSchema.nullable(),
  explanation: z.string().nullable(),
});
export type JobItemPuzzle = z.infer<typeof JobItemPuzzle>;

export const StudioJobItem = z.object({
  id: z.union([z.string(), z.number()]).transform(String),
  seq: z.number().int(),
  status: ItemStatus,
  verification: VerificationResult,
  /** Short key (answer word, host, sourceId…) used to avoid repeats. */
  label: z.string().nullable(),
  /** Rejected items only: `{ spec, answer, explanation, raw }` as the engine returned them (spec may be null). */
  candidate: z.unknown().nullable().default(null),
  createdAt: z.string(),
  puzzle: JobItemPuzzle.nullable(),
});
export type StudioJobItem = z.infer<typeof StudioJobItem>;

/** A pack a levelpack job built, and where it is now. */
export const StudioJobPack = z.object({
  track: z.string(),
  index: count,
  status: z.enum(["draft", "published", "deleted"]),
  first: z.number().int(),
  last: z.number().int(),
  chapter: z.string().nullable(),
  sha256: z.string().nullable(),
  verified: z.object({ checked: count, ok: count }).nullable(),
});
export type StudioJobPack = z.infer<typeof StudioJobPack>;

export const StudioJobDetail = z.object({ job: StudioJob, items: z.array(StudioJobItem), packs: z.array(StudioJobPack).optional() });
export type StudioJobDetail = z.infer<typeof StudioJobDetail>;

// ---------------------------------------------------------------- usage

const TokenTotals = z.object({ input: count, output: count, cacheRead: count, cacheWrite: count });

export const StudioUsage = z.object({
  month: z.string(),
  calls: count,
  aiJobs: count,
  tokens: TokenTotals,
  costUsd: z.number().min(0),
  budgetUsd: z.number().min(0),
  remainingUsd: z.number().min(0),
  aiEnabled: z.boolean(),
  byModel: z.array(z.object({ model: z.string(), calls: count, tokens: TokenTotals, costUsd: z.number().min(0) })),
});
export type StudioUsage = z.infer<typeof StudioUsage>;

// ---------------------------------------------------------------- bank

/** `GET /puzzles?mode=bank` rows: the Studio fields are required here. */
export const BankListItem = z.object({
  id: z.string(),
  puzzleNumber: z.number().int().nullable(),
  date: isoDate.nullable(),
  type: z.string(),
  category: Category,
  difficulty: Difficulty,
  status: PuzzleStatus,
  mode: PuzzleRowMode,
  source: PuzzleSource,
  reviewRequired: z.boolean(),
  reviewState: ReviewState,
  verification: VerificationSummary,
});
export type BankListItem = z.infer<typeof BankListItem>;

export const BankList = z.object({ items: z.array(BankListItem), nextCursor: z.string().nullable() });
export type BankList = z.infer<typeof BankList>;

export const BankItem = BankListItem.extend({ approvedBy: z.string().nullable(), approvedAt: z.string().nullable() });
export type BankItem = z.infer<typeof BankItem>;

/** POST /puzzles/:id/verify and PATCH /puzzles/:id/spec. */
export const BankVerifyResult = z.object({ puzzle: BankItem, verification: VerificationResult });
export type BankVerifyResult = z.infer<typeof BankVerifyResult>;

/** POST /puzzles/:id/schedule: the placed puzzle and the day's previous one, now back in the bank. */
export const BankScheduleResult = z.object({ puzzle: BankItem, displaced: BankItem.nullable() });
export type BankScheduleResult = z.infer<typeof BankScheduleResult>;

export type ScheduleTarget = "daily" | "hard";
